// /admin — the console shell.
//
// Renders every admin screen as a card, GROUPED BY THE RANK IT NEEDS, least privileged
// first. The grouping and the card list are pure and live in ./console-sections.js, which
// is also where the reasoning for each non-descriptor screen is written down; this file
// only draws what that module returns.
//
// Why grouped rather than one flat grid: the index mixes a lead's screens with an admin's,
// and it used to interleave them in registry order with a small rank chip on each card. A
// lead looking for the two things they can actually use had to read every card. Now the
// page's own shape says it — your screens at the top, a gap, then the ones that need a
// rank you may not have — and the chip is gone because the heading says it better.
//
// PRESENTATION, NEVER AUTHORIZATION. Nothing here decides access. The nav hides the link
// and this page explains itself, but `requireProductRole('support', 'lead')` on
// src/routes/api/admin/index.js is what refuses — which is why this component happily
// mounts for an agent who types the URL and simply reports what the API said. The same is
// true of the groups: an admin-only card is drawn for a lead, under a heading that tells
// them why clicking it will not work.
//
// No `mx-auto max-w-7xl px-…` wrapper: AppLayout's <main> already is that container
// (../../layout/AppLayout.jsx), and repeating it here would nest one max-width inside
// another and double the page padding. Every other page relies on the same thing.
//
// Every colour is a semantic token (bg-surface, text-ink, border-line) so the screen flips
// theme without a `dark:` variant — see the note at the top of web/src/styles/app.css.

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { apiFetch } from '../../api/client.js';
import { useAtLeastRole } from '../../session/SessionProvider.jsx';
import { consoleErrorMessage } from './descriptor-form.js';
import { consoleSections } from './console-sections.js';

function ConsoleCard({ entry }) {
  return (
    <li>
      <Link
        to={entry.to}
        className="block rounded-lg border border-line bg-surface p-4 hover:bg-sunken
                   focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
      >
        <span className="block text-sm font-medium text-ink">{entry.label}</span>
        {entry.hint && (
          <p className="mt-1 truncate text-xs text-ink-secondary" title={entry.hint}>
            {entry.hint}
          </p>
        )}
      </Link>
    </li>
  );
}

export default function AdminConsole() {
  const isLead = useAtLeastRole('support', 'lead');
  const [state, setState] = useState({ status: 'loading', resources: [], error: null });

  useEffect(() => {
    const controller = new AbortController();
    apiFetch('/admin', { signal: controller.signal })
      .then((body) => setState({ status: 'ready', resources: body.data, error: null }))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState({
          status: 'error',
          resources: [],
          // FORBIDDEN and PRODUCT_NOT_LICENSED are distinct codes on the same
          // 403 and the remediation differs; `consoleErrorMessage` is the one
          // place that distinction is made, and it is tested
          // (test/web/descriptor-form.test.js).
          error: consoleErrorMessage(error),
        });
      });
    return () => controller.abort();
  }, []);

  // Built from whatever the index request did. On an error `resources` is empty and the
  // three static screens still render — a lead whose index failed to load still has a
  // working schedule editor, and that was true before the grouping and stays true now.
  const sections = consoleSections(state.resources);

  return (
    <div>
      <h1 className="text-xl font-semibold text-ink">Admin</h1>

      {!isLead && (
        <p className="mt-2 text-sm text-ink-secondary">
          This section is for support leads and administrators.
        </p>
      )}

      {state.status === 'error' && (
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {state.error}
        </div>
      )}

      {/* `space-y-8` is the buffer between groups and is deliberately larger than the
          `gap-3` between cards: the whole point is that the eye reads the gap as "these
          are a different kind of thing", which a gap the same size as the one between
          cards cannot do. */}
      <div className="mt-6 space-y-8">
        {sections.map((section) => (
          <section key={section.requires}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
              {section.label}
            </h2>
            <ul className="mt-3 grid gap-3 sm:grid-cols-2">
              {section.entries.map((entry) => (
                <ConsoleCard key={entry.key} entry={entry} />
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/*
        The empty-list note. This branch did not exist until the registry had something in
        it, and its absence was invisible: the shell handled `!isLead`, `error` and the
        EMPTY list, so the moment `macros` was registered the component fell through every
        branch and rendered the <h1> alone. A blank page under a heading reads as a broken
        build, not as "no editor yet". Neither task's tests caught it — the shell's cover
        the pure nav and rank modules, the registry's cover the server, and nothing
        rendered this component with a non-empty list.

        It survives the grouping because the three static screens mean `sections` is never
        empty, so this now says what it actually means: the REGISTRY is empty, not the page.
      */}
      {state.status === 'ready' && state.resources.length === 0 && (
        <p className="mt-6 text-sm text-ink-muted">No other resources are editable here yet.</p>
      )}
    </div>
  );
}
